import React, { useState, useEffect } from "react";

const TERMINAL_HINTS = {
  "S1-P2": "✓ Stimulus visuel → amygdale active",
  "S1-P3": "✓ Entrée visuelle → relai thalamique",
  "S2-P2": "✓ Signal auditif → amygdale validée",
  "S2-P1": "✓ Stimulus auditif → cortex préfrontal",
  "P1-M1": "✓ Cortex → hippocampe synchronisé",
  "P1-M2": "✓ Traitement cognitif → cortex cingulaire",
  "P2-M1": "✓ Amygdale → mémoire hippocampique",
  "P2-M2": "✓ Émotion → cortex cingulaire activé",
  "P3-MOD": "✓ Relai thalamique → modulateur cognitif",
  "MOD-E1": "✓ Modulation → réponse empathique",
  "M1-E1": "✓ Hippocampe → empathie exprimée",
  "M1-E2": "✓ Mémoire → gratitude déclenchée",
  "M2-E1": "✓ Cortex cingulaire → empathie",
  "M2-E2": "✓ GRATITUDE PATHWAY ACTIVÉ",
};

// targetPseudo : pseudo du joueur à qui communiquer le mot de passe
// En 6J c'est le joueur 5, en 4J c'est le joueur 1 (ou le joueur courant lui-même)
export default function GratitudeTerminal({
  connections = [],
  onSolved,
  password = "",
  solved = false,
  targetPseudo = null, // ← nouveau : passé depuis NeuralPuzzleContainer
}) {
  const [logs, setLogs] = useState([
    { type: "info", text: "=== TERMINAL DE VALIDATION EMPATHIQUE ===" },
    { type: "info", text: "Analyseur de circuit neural v2.4" },
    { type: "info", text: "En attente de connexions synaptiques..." },
  ]);
  const [gratitudeLevel, setGratitudeLevel] = useState(0);
  const [localSolved, setLocalSolved] = useState(false);
  const [prevConnectionCount, setPrevConnectionCount] = useState(0);

  useEffect(() => {
    const correctCount = connections.filter(conn => {
      const key1 = `${conn[0]}-${conn[1]}`;
      const key2 = `${conn[1]}-${conn[0]}`;
      return TERMINAL_HINTS[key1] || TERMINAL_HINTS[key2];
    }).length;

    setGratitudeLevel(Math.round((correctCount / 14) * 100));

    if (correctCount === 14 && !localSolved) {
      setLocalSolved(true);
      setLogs(prev => [
        ...prev,
        { type: "success", text: "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━" },
        { type: "success", text: "CIRCUIT NEURONAL COMPLET" },
        { type: "success", text: "Validation Empathie : 100%" },
        { type: "success", text: "Gratitude Pathway : ACTIVÉ" },
        { type: "success", text: "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━" },
        { type: "password", text: `Mot de passe généré : NEURAL` },
      ]);
      onSolved?.("NEURAL");
    }
  }, [connections, localSolved, onSolved]);

  useEffect(() => {
    if (connections.length === 0 && prevConnectionCount > 0) {
      setLogs(prev => [
        ...prev,
        { type: "warning", text: "⚠ CIRCUIT RÉINITIALISÉ - Connexion invalide détectée" },
        { type: "info", text: "Reprise de l'analyse..." },
      ]);
    }
    setPrevConnectionCount(connections.length);
  }, [connections.length, prevConnectionCount]);

  useEffect(() => {
    if (connections.length === 0 || localSolved) return;
    const lastConn = connections[connections.length - 1];
    const key1 = `${lastConn[0]}-${lastConn[1]}`;
    const key2 = `${lastConn[1]}-${lastConn[0]}`;
    const hint = TERMINAL_HINTS[key1] || TERMINAL_HINTS[key2];
    if (hint && prevConnectionCount < connections.length) {
      setLogs(prev => [...prev, { type: "success", text: hint }]);
    }
  }, [connections, localSolved, prevConnectionCount]);

  // Label du destinataire du mot de passe
  const recipientLabel = targetPseudo || "le joueur concerné";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[350px_1fr] gap-4">
      {/* Jauge + mot de passe */}
      <div className="space-y-4">
        <div className="rounded-xl border border-white/10 bg-slate-950/60 p-5">
          <div className="flex flex-col items-center gap-4">
            <span className="text-xs text-slate-400 uppercase tracking-wider">Niveau de Gratitude</span>
            <div className="text-5xl font-bold font-mono text-emerald-300">{gratitudeLevel}%</div>
            <div className="w-full h-4 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-500 rounded-full relative"
                style={{ width: `${gratitudeLevel}%` }}
              >
                <div className="absolute inset-0 animate-pulse bg-white/20" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center">
          {solved ? (
            <div className="w-full rounded-xl border border-emerald-500/50 bg-emerald-950/40 p-5">
              <div className="text-center">
                <div className="text-xs text-emerald-300 mb-3">✅ Circuit neuronal validé</div>
                <div className="text-4xl font-mono font-bold text-white mb-2 animate-pulse">{password}</div>
                <div className="text-xs text-slate-400">
                  Communique ce mot de passe à{" "}
                  <span className="text-slate-200 font-semibold">{recipientLabel}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full rounded-xl border border-slate-700 bg-slate-900/40 p-5">
              <div className="text-center text-slate-500 text-sm">
                Mot de passe en attente de validation du circuit
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Terminal logs */}
      <div className="rounded-xl border border-white/10 bg-black/80 p-5">
        <div className="text-xs text-slate-400 uppercase tracking-wider mb-3">Terminal de Validation</div>
        <div className="h-[280px] overflow-y-auto font-mono text-xs space-y-1">
          {logs.map((log, i) => (
            <div key={i} className={
              log.type === "success" ? "text-emerald-400" :
              log.type === "error"   ? "text-red-400" :
              log.type === "warning" ? "text-amber-400" :
              log.type === "password"? "text-cyan-300 font-bold" :
              "text-slate-400"
            }>
              {log.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}